import { isPaymentGuaranteeType } from '../state/types';
import { PUBLIC_SEARCH_MODE, type PublicSearchMode } from '@shared/types/restaurant';
import type { BookingStep, BookingStepId, FlowContext } from './types';

/**
 * PERSONALIZE step's isRequired predicate. Extracted so CONFIRMATION's
 * fallback logic can reuse the exact same condition without duplicating it.
 * Exported for unit tests that pin the contract.
 */
export function isPersonalizeRequired(ctx: FlowContext): boolean {
  return ctx.hasPersonalizationOptions;
}

/**
 * GUARANTEE step's isRequired predicate. Same extraction rationale as
 * `isPersonalizeRequired`. Mirrors the original inline check exactly —
 * agents bypass payment, users with a card token already collected bypass
 * payment, slots without a positive guarantee fee or non-payment guarantee
 * types bypass payment.
 */
export function isGuaranteeRequired(ctx: FlowContext): boolean {
  if (ctx.isAgent) return false;
  if (ctx.hasCardToken) return false;
  if (ctx.guaranteeFee <= 0) return false;
  return isPaymentGuaranteeType(ctx.selectedSlotType);
}

/**
 * Inputs that drive the EXPERIENCE step's visibility. Kept narrow on purpose
 * so every call site can compute these values from its own source:
 * `steps.ts` EXPERIENCE.isRequired (from FlowContext), the slot handlers in
 * `hooks/useBookingFlow.ts` (`handleSelectTime`,
 * `handleSelectCrossServiceSlot` — from raw slot data, before BookingState
 * commits), and `SelectAreaPage.handleSelectArea` (from the area-filtered
 * menus).
 */
export interface ShouldShowExperienceInputs {
  isWaitingList: boolean;
  preSelectedMenuId: number | null;
  forceShowMenuSelection: boolean;
  /**
   * The guest's committed menu is not offered among the candidates (see
   * `applySelectedMenuFilter`). Forces the step so they choose from what is
   * available rather than being auto-assigned a substitute they never picked.
   * Defaults to false so existing call sites keep their behaviour.
   */
  menuFilterMismatched?: boolean;
  /** Restaurant search mode — gates the REQ-2 area-mode guarantee auto-skip */
  publicSearchMode: PublicSearchMode;
  menuCount: number;
  singleMenuHasGuarantee: boolean;
  singleMenuHasPrice: boolean;
  singleMenuIsEvent: boolean;
  isSpecialDay: boolean;
}

/**
 * EXPERIENCE step predicate. Shared by `steps.ts` EXPERIENCE.isRequired and
 * the imperative sites that decide whether to navigate directly to
 * /select-experience instead of advancing (see the inputs doc above for the
 * roster). Single source of truth — changing the rule here updates every
 * call site.
 */
export function shouldShowExperience(i: ShouldShowExperienceInputs): boolean {
  if (i.isWaitingList) return false;
  // Ordered ABOVE preSelectedMenuId deliberately. That rule means "the guest
  // already has a menu, don't ask again" — but a preselected menu that is not
  // offered here is exactly the case that must be re-asked, and it is the
  // dominant producer of selectedMenuFilter (hooks/useBookingFlow re-imposes
  // the filter from it on every /select-time mount). Checking it first left the
  // mismatch rule unreachable on the common path while the handlers still
  // cleared the experience — committing `menu: 0` with the guarantee derived
  // from an unrelated menu.
  if (i.menuFilterMismatched) return true;
  if (i.preSelectedMenuId) return false;
  if (i.forceShowMenuSelection) return true;
  // REQ-2 (force-show-area-selection spec §2): in area mode ONLY, a single
  // available menu whose only show-trigger is the payment guarantee no longer
  // forces the step — the guarantee is disclosed where it is enforced (the
  // GUARANTEE step renders the fee before card entry). Price / event /
  // special-day triggers still show the step, and menu-mode restaurants are
  // untouched (`menu_area` is treated as menu-mode and skips this branch).
  if (
    i.publicSearchMode === PUBLIC_SEARCH_MODE.AREA &&
    i.menuCount === 1 &&
    i.singleMenuHasGuarantee &&
    !i.singleMenuHasPrice &&
    !i.singleMenuIsEvent &&
    !i.isSpecialDay
  ) {
    return false;
  }
  if (i.menuCount >= 2) return true;
  if (i.singleMenuHasGuarantee) return true;
  if (i.singleMenuHasPrice) return true;
  if (i.singleMenuIsEvent) return true;
  if (i.isSpecialDay) return true;
  return false;
}

/**
 * Inputs that drive the AREA step's visibility. Kept narrow on purpose so it
 * can be called from two kinds of places that compute these values from
 * different sources: `steps.ts` AREA.isRequired (from FlowContext) and the
 * three imperative slot handlers in `hooks/useBookingFlow.ts`
 * (handleSelectTime / handleSelectCrossServiceSlot /
 * handleSelectAlternativeMenu — from raw slot data, before BookingState
 * commits).
 */
export interface ShouldShowAreaStepInputs {
  isWaitingList: boolean;
  publicSearchMode: PublicSearchMode;
  /** Selected slot has at least one area (after the Flow D areaFilterIds intersection) */
  hasAvailableAreas: boolean;
  /** Exactly one BOOKABLE area — routing auto-assigns it and skips the page */
  areaAutoAssigned: boolean;
  /**
   * REQ-1 of the force-show-area-selection spec: restaurant-level flag that
   * shows the step even when a single area would auto-assign, so the guest
   * confirms the area explicitly instead of silent auto-assignment.
   */
  forceShowAreaSelection: boolean;
}

/**
 * AREA step predicate — single source of truth, shared between `steps.ts`
 * AREA.isRequired and the three copy-pasted slot handlers in
 * `hooks/useBookingFlow.ts` (which decide imperatively whether to navigate to
 * /select-area or auto-assign and skip). Changing the rule here updates all
 * four call sites; never inline this logic at any of them.
 *
 * Only 'area' mode activates the step ('menu_area' is reserved on the backend
 * but not implemented end-to-end — treated as menu-mode). The
 * `forceShowAreaSelection` flag only overrides the single-area auto-assign
 * skip; it never overrides the waiting-list, mode, or no-areas gates.
 */
export function shouldShowAreaStep(i: ShouldShowAreaStepInputs): boolean {
  if (i.isWaitingList) return false;
  if (i.publicSearchMode !== PUBLIC_SEARCH_MODE.AREA) return false;
  if (!i.hasAvailableAreas) return false;
  if (i.areaAutoAssigned && !i.forceShowAreaSelection) return false;
  return true;
}

/**
 * GUARANTEE step's route. Owned by the step table; exported for
 * `useReservationCommit`'s decline redirect, which must navigate here
 * WITHOUT `goToStep` (its `activeSteps` closure still has GUARANTEE
 * filtered out at that point — see the hook's comment).
 */
export const GUARANTEE_PATH = '/payment';

/**
 * All booking steps in canonical order with their conditions.
 *
 * The order here defines the navigation sequence. Each step has an
 * `isRequired` function that determines whether it should be shown
 * based on the current flow context (booking state + restaurant config + slot data).
 *
 * Steps that evaluate to `isRequired: false` are skipped entirely
 * — the orchestrator jumps from the previous step to the next required step.
 */
export const BOOKING_STEPS: BookingStep[] = [
  {
    id: 'DINERS',
    path: '/select-diners',
    isRequired: (ctx: FlowContext) => {
      // Skip when rescheduling — party size is pre-set from the original reservation
      if (ctx.isReschedule) return false;
      return true;
    },
  },

  {
    id: 'DATE',
    path: '/select-date',
    isRequired: (ctx: FlowContext) => {
      // Skip when the date was supplied by a deep link and is locked.
      if (ctx.isDateLocked) return false;
      // Skip when event has exactly 1 date (auto-selected)
      if (ctx.isEventFlow && ctx.eventDateCount === 1) return false;
      return true;
    },
  },

  {
    id: 'SERVICE',
    path: '/select-service',
    isRequired: (ctx: FlowContext) => {
      // Skip when event has exactly 1 service type (auto-selected)
      if (ctx.isEventFlow && ctx.eventServiceCount === 1) return false;
      // Skip if only 1 service type has real availability for the selected date
      // (or 1 static service type when no date selected yet). Auto-select it.
      // Show if 0 (edge case: let the page handle the empty state) or 2+.
      return ctx.availableServiceCount !== 1;
    },
  },

  {
    id: 'EARLY_ACCESS',
    path: '/early-access',
    isRequired: (ctx: FlowContext) => {
      // Show only when the selected date is a request/early-access day
      return ctx.isEarlyAccessFlow && !ctx.isWaitingList;
    },
  },

  {
    id: 'MENU_FILTER',
    path: '/select-menu-filter',
    isRequired: (ctx: FlowContext) => {
      // Skip when marketplace menu is pre-selected
      if (ctx.meitreNetworkMenuId) return false;
      // Skip when menu is pre-selected from landing page
      if (ctx.preSelectedMenuId) return false;
      // Step 3C: show when restaurant allows experience selection AND there are 2+ menus
      return ctx.allowExperienceSelection && ctx.menuCount > 1;
    },
  },

  {
    id: 'AREA_FILTER',
    path: '/select-area-filter',
    isRequired: (ctx: FlowContext) => {
      if (ctx.meitreNetworkMenuId) return false;
      if (ctx.preSelectedMenuId) return false;
      // Flow D: only area-mode restaurants that opted INTO the pre-timeslot
      // filter, and that publish 2+ areas, get this step.
      //
      // `allowAreaFilter` decouples area *filtering* from area *selection*:
      // `publicSearchMode === 'area'` alone used to imply the filter, which
      // conflated the two. The backend backfilled the flag to `true` for every
      // restaurant already in area mode, so gating on it changes nothing for
      // any live restaurant. It does NOT gate the post-timeslot AREA step —
      // see shouldShowAreaStep.
      if (ctx.publicSearchMode !== PUBLIC_SEARCH_MODE.AREA) return false;
      if (!ctx.allowAreaFilter) return false;
      return ctx.publicAreas.length > 1;
    },
  },

  {
    id: 'TIME',
    path: '/select-time',
    isRequired: () => true,
  },

  {
    id: 'SIGN_IN',
    path: '/sign-in',
    isRequired: (ctx: FlowContext) => {
      // Show only when the user is NOT authenticated.
      // Once authenticated, this step is skipped automatically.
      return !ctx.isAuthenticated;
    },
  },

  {
    id: 'COMPLETE_PROFILE',
    path: '/complete-profile',
    isRequired: (ctx: FlowContext) => {
      // Show when authenticated user is missing name OR phone.
      // Once both are provided, this step is skipped automatically.
      return ctx.isAuthenticated && (!ctx.hasPhone || !ctx.hasName);
    },
  },

  {
    id: 'WL_FORM',
    path: '/waiting-list',
    isRequired: (ctx: FlowContext) => ctx.isWaitingList && !ctx.isReschedule,
  },

  {
    id: 'WL_CALENDAR',
    path: '/waiting-list-calendar',
    isRequired: (ctx: FlowContext) => ctx.isWaitingList && !ctx.isReschedule,
  },

  {
    id: 'WL_ALTERNATIVES',
    path: '/waiting-list-alternatives',
    isRequired: (ctx: FlowContext) => ctx.isWaitingList && !ctx.isReschedule && ctx.hasWlAlternatives,
  },

  {
    id: 'AREA',
    path: '/select-area',
    isRequired: (ctx: FlowContext) =>
      shouldShowAreaStep({
        isWaitingList: ctx.isWaitingList,
        publicSearchMode: ctx.publicSearchMode,
        hasAvailableAreas: ctx.hasAvailableAreas,
        areaAutoAssigned: ctx.areaAutoAssigned,
        forceShowAreaSelection: ctx.forceShowAreaSelection,
      }),
  },

  {
    id: 'EXPERIENCE',
    path: '/select-experience',
    isRequired: (ctx: FlowContext) =>
      shouldShowExperience({
        isWaitingList: ctx.isWaitingList,
        preSelectedMenuId: ctx.preSelectedMenuId,
        forceShowMenuSelection: ctx.forceShowMenuSelection,
        // Must match the handlers: they navigate here directly on a mismatch,
        // so if the engine disagreed the step would be absent from activeSteps,
        // currentStepIndex would be -1, and Continue would silently no-op.
        menuFilterMismatched: ctx.menuFilterMismatched,
        publicSearchMode: ctx.publicSearchMode,
        menuCount: ctx.selectedSlotMenus.length,
        singleMenuHasGuarantee: ctx.singleMenuHasGuarantee,
        singleMenuHasPrice: ctx.singleMenuHasPrice,
        singleMenuIsEvent: ctx.singleMenuIsEvent,
        isSpecialDay: ctx.isSpecialDay,
      }),
  },

  {
    id: 'MARIDAJE',
    path: '/maridaje',
    isRequired: (ctx: FlowContext) => {
      return ctx.hasPairings && ctx.selectedMenuAllowsPairings;
    },
  },

  {
    id: 'PERSONALIZE',
    path: '/personalize',
    isRequired: isPersonalizeRequired,
  },

  {
    id: 'AGENT_GUEST_INFO',
    path: '/agent-guest-info',
    isRequired: (ctx: FlowContext) => ctx.isAgent,
  },

  {
    id: 'GUARANTEE',
    path: GUARANTEE_PATH,
    isRequired: isGuaranteeRequired,
  },

  {
    id: 'CONFIRMATION',
    path: '/confirmation',
    /**
     * Phase 2 of Confirm-step removal: drop the dedicated confirmation page
     * when the per-tenant `bookingSkipConfirmationV2` flag is on. The flag
     * is frozen at session start by `BookingProvider` (T11) and surfaced as
     * `ctx.skipConfirmStep`. The step set is stable for the lifetime of the
     * session. When CONFIRMATION drops, the previous step (Personalize if
     * enabled, else Guarantee) becomes the commit host — `useCommitOrAdvance`
     * dispatches on "next step is BOOKING_FINAL" rather than the flag.
     *
     * The "commit-host predecessor must exist" invariant is enforced in
     * `getActiveSteps` below — NOT here. This predicate intentionally only
     * encodes the per-tenant flag intent. Cross-step invariants (like
     * "BOOKING_FINAL must be preceded by a commit-capable page") belong at
     * the assembly site where the full step list is visible.
     */
    isRequired: (ctx: FlowContext) => !ctx.skipConfirmStep,
  },

  {
    id: 'BOOKING_FINAL',
    path: '/booking-final',
    isRequired: () => true,
  },
];

/**
 * Set of step IDs whose pages integrate `useCommitOrAdvance` or
 * `useReservationCommit` and therefore CAN fire the reservation commit
 * POST when they are positioned immediately before BOOKING_FINAL in the
 * active flow.
 *
 * Keep this in sync with the actual page implementations:
 *  - PERSONALIZE → `PersonalizePage` uses `useCommitOrAdvance`
 *  - GUARANTEE → `StripePaymentForm` / `MercadoPagoPaymentForm` use
 *    `useCommitOrAdvance` (both mount at `/payment`)
 *  - CONFIRMATION → `ConfirmationPage` uses `useReservationCommit` directly
 *
 * Every OTHER step is "advance-only" — its continue/submit handler calls
 * `goToNext()` or `navigate()` without commit logic. If any advance-only
 * step ends up as BOOKING_FINAL's predecessor, no commit ever fires and
 * the user lands on `/booking-final` showing a "Reservation Confirmed"
 * surface for a reservation that doesn't exist. This was reported by ops
 * 2026-05-25 against `beastybutchers` (config produced a flow ending
 * `EXPERIENCE → BOOKING_FINAL` with no commit-host between).
 *
 * Exported for the cross-product invariant tests in `steps.test.ts`.
 */
export const COMMIT_CAPABLE_STEP_IDS: ReadonlySet<string> = new Set([
  'PERSONALIZE',
  'GUARANTEE',
  'CONFIRMATION',
]);

/**
 * Get the list of active steps for a given context, with the
 * commit-host-predecessor invariant enforced.
 *
 * INVARIANT: for any context, the step immediately before BOOKING_FINAL in
 * the returned list MUST be in `COMMIT_CAPABLE_STEP_IDS`. If the natural
 * filtering would leave an advance-only step as BOOKING_FINAL's predecessor
 * (e.g. `AGENT_GUEST_INFO`, `EARLY_ACCESS`, `MARIDAJE`, `EXPERIENCE`), this
 * function injects `CONFIRMATION` immediately before BOOKING_FINAL so the
 * commit always has a home.
 *
 * This is the architectural fix for the 2026-05-25 production bug. Initial
 * fix that day added a fallback to `CONFIRMATION.isRequired` checking only
 * for `!isPersonalizeRequired && !isGuaranteeRequired` — that handled the
 * `beastybutchers` configuration (no personalization + traditional slots)
 * but missed agent reservations on skip-confirm tenants with
 * personalization, where `AGENT_GUEST_INFO` lands between `PERSONALIZE`
 * and `BOOKING_FINAL` as an advance-only step. The invariant lives here so
 * it captures POSITION, not just PRESENCE, and is exhaustively testable.
 */
export function getActiveSteps(ctx: FlowContext): BookingStep[] {
  const filtered = BOOKING_STEPS.filter((step) => step.isRequired(ctx));

  const finalIdx = filtered.findIndex((s) => s.id === 'BOOKING_FINAL');
  // If BOOKING_FINAL isn't in the list (shouldn't happen — it's
  // always-required), there's nothing to enforce.
  if (finalIdx <= 0) return filtered;

  const predecessor = filtered[finalIdx - 1];
  if (COMMIT_CAPABLE_STEP_IDS.has(predecessor.id)) {
    // Invariant already satisfied by the natural filter.
    return filtered;
  }

  // Predecessor is advance-only. Inject CONFIRMATION right before
  // BOOKING_FINAL as the fallback commit host.
  const confirmStep = BOOKING_STEPS.find((s) => s.id === 'CONFIRMATION');
  if (!confirmStep) return filtered; // defensive — should never happen
  return [
    ...filtered.slice(0, finalIdx),
    confirmStep,
    ...filtered.slice(finalIdx),
  ];
}

/**
 * Determine whether a guarantee is required based on type and fee.
 * Exported for use outside the flow (e.g., confirmation page logic).
 */
export function isPaymentGuarantee(
  type: string | null | undefined,
  fee: number | null | undefined,
): boolean {
  return isPaymentGuaranteeType(type) && (fee ?? 0) > 0;
}

/**
 * Resolve the next active step after `currentPath`, computed purely from the
 * given context — NO React render snapshot, no ref, no flushSync.
 *
 * This is the render-independent core of the flow's forward navigation. Callers
 * that have just selected something (a time, an area) build a FlowContext from
 * the values they're committing and pass it here, so the next-step decision
 * reflects the new selection immediately instead of the previous render's
 * snapshot. Returns null when `currentPath` isn't an active step or is already
 * the last one.
 */
export function resolveNextStep(
  ctx: FlowContext,
  currentPath: string,
): BookingStep | null {
  const steps = getActiveSteps(ctx);
  const idx = steps.findIndex((s) => s.path === currentPath);
  if (idx < 0 || idx >= steps.length - 1) return null;
  return steps[idx + 1];
}

/**
 * Resolve the first active step canonically AFTER `fromStepId` — the explicit
 * advance for a handler whose own step may have just invalidated itself.
 *
 * Why this exists: `activeSteps` recomputes from live state, so a page that
 * mutates state can remove its OWN step while still mounted on it (e.g. the
 * alternative-time switch on /select-experience landing on a slot whose single
 * plain menu makes EXPERIENCE unnecessary). `resolveNextStep`/`getNextStep`
 * then correctly return null — the current path is not an active step — and a
 * `goToNext()` wired to them silently no-ops: an enabled Continue button that
 * does nothing, with Back exiting to `/` and session-restore defeating reload.
 * Same class as the 2026-05-21 cardToken/GUARANTEE bug (see useCommitOrAdvance)
 * and the WL->standard switch (see WaitingListAlternativesPage).
 *
 * When `fromStepId`'s step IS still active this is exactly `resolveNextStep`
 * from its path (active steps preserve canonical order, including the injected
 * CONFIRMATION). When it is not, it resolves the step the guest would have
 * reached anyway.
 *
 * CONTRACT — this is NOT a general recovery mechanism. It only looks FORWARD
 * from the caller's canonical position, so the caller must (a) know its own
 * step id and (b) not have un-satisfied any step at or before it. The pinned
 * counterexample: switching waiting-list -> standard makes TIME required
 * canonically BEFORE the disappearing WL_ALTERNATIVES step — advancing from
 * there would skip it, which is why WaitingListAlternativesPage jumps with
 * goToStep('TIME') instead. Deliberately NOT folded into resolveNextStep /
 * getNextStep as a fallback: a default fall-forward would turn stale deep
 * links into bypasses past diners/date/auth and change the commit-vs-advance
 * meaning getNextStep has in useCommitOrAdvance.
 */
export function resolveNextStepAfter(
  ctx: FlowContext,
  fromStepId: BookingStepId,
): BookingStep | null {
  const canonicalIdx = BOOKING_STEPS.findIndex((s) => s.id === fromStepId);
  if (canonicalIdx < 0) return null;
  const active = getActiveSteps(ctx);
  return (
    active.find(
      (s) => BOOKING_STEPS.findIndex((b) => b.id === s.id) > canonicalIdx,
    ) ?? null
  );
}

/**
 * Map a URL pathname to its canonical booking step id, if it matches one.
 * Returns null for paths outside the booking flow.
 */
export function getStepIdFromPath(pathname: string): string | null {
  const match = BOOKING_STEPS.find((s) => s.path === pathname);
  return match?.id ?? null;
}
